import { PropertyValueMap, TemplateResult, html, CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksRowCellSelectEvent } from '../../events';
import { BlocksElement } from '../BlocksElement';
import { BlocksTableCell } from '../table-cell/table-cell';
import { BlocksTableHeaderCell } from '../table-header-cell/table-header-cell';
import type { TableRowDragPosition } from './table-row.model';
import styles from './table-row.styles';

/**
 * @slot default - The table row content
 * @fires bl-table-cell-change - emitted when a row content change
 * @csspart base - The component's base wrapper.
 * @csspart wrap - The component's wrapper.
 * @csspart part-default - The component's default slot.
 */
export class BlocksTableRow extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Aria level attribute
   * - The aria level attribute defines the hierarchical level of an element within a structure
   */
  @property({ type: Number })
  isAriaLevel: number | undefined;

  /**
   * DataKey
   * - Key attribute for row IDs
   */
  @property({ type: String })
  dataKey: string | undefined;

  /**
   * Drag hover
   * - Sets the drag hover position when a row is being actively dragged
   */
  @property({ type: String })
  dragHover: TableRowDragPosition;

  /**
   * Disabled attribute
   * - If true, sets the state of the row to disabled
   */
  @property({ type: Boolean })
  disabled: boolean | undefined;

  /**
   * Expandable
   * - If true, makes the row expandable and adds an accordion arrow to the first cell. To be added in the 1st level row (parent row)
   */
  @property({ type: Boolean, reflect: true })
  isExpandable: boolean | undefined;

  /**
   * Expandable active
   * - If true, rotates the accordion arrow on the first cell
   */
  @property({ type: Boolean })
  isExpandableActive: boolean | undefined;

  /**
   * Expandable child
   * - If true, adds a class to indicate it's an expandable child row. This should be added to the 2nd level row (child row)
   */
  @property({ type: Boolean, reflect: true })
  isExpandableChild: boolean | undefined;

  /**
   * Expandable child active
   * - If true, expands the child row. This should be added to the 2nd level row. When added, it will display the child row content.
   */
  @property({ type: Boolean })
  isExpandableChildActive: boolean | undefined;

  /**
   * Expandable nested
   * - If true, makes the nested row expandable and adds an accordion arrow to the first cell. To make the child row as expandable, this should be added to the 2nd level row.
   */
  @property({ type: Boolean })
  isExpandableNested: boolean | undefined;

  /**
   * Expandable nested child
   * - If true, adds a class to indicate it's an expandable nested child row. Need to add it to the 3rd level row (nested child row)
   */
  @property({ type: Boolean, reflect: true })
  isExpandableNestedChild: boolean | undefined;

  /**
   * Expandable nested child active
   * - If true, expands the nested child row. This should be added to the 3rd level row. When added, it will display the nested child row content.
   * - Need to add `isExpandableChildActive` also to work.
   */
  @property({ type: Boolean })
  isExpandableNestedChildActive: boolean | undefined;

  /**
   * Draggable
   * - If true, adds the icon for dragging
   */
  @property({ type: Boolean })
  isDraggable: boolean | undefined;

  /**
   * Self foldable
   * - If true, on screen resize the overflowing table header cell and table cell will fold into the self foldable child row
   */
  @property({ type: Boolean })
  isSelfFoldable: boolean | undefined;

  /**
   * Self foldable child
   * - If true, the row that holds the overflowing table header cell and table cells
   */
  @property({ type: Boolean, reflect: true })
  isSelfFoldableChild: boolean | undefined;

  /**
   * Bulk actions
   * - If true, adds the checkbox for bulk actions
   */
  @property({ type: Boolean })
  isBulkAction: boolean | undefined;

  /**
   * Bulk action index
   * - Sets the index for events
   */
  @property({ type: Number })
  bulkActionIndex: number | undefined;

  /**
   * Bulk action selected
   * - Sets the state selected
   */
  @property({ type: Boolean })
  isBulkActionSelected: boolean | undefined;

  /**
   * Mobile view
   * - Enables the mobile view which moves the overflowing cell content to a mobile modal on small screens only
   */
  @property({ type: Boolean })
  isMobileView: boolean | undefined;

  /**
   * Loading State
   * - Enables the skeleton loader on cells
   */
  @property({ type: Boolean })
  isLoading = false;

  /**
   * Internal property
   * To hide the self-foldable row
   * - Boolean property used to hide the row when no cells are self-folded inside the row
   * @deprecated
   */
  @property({ type: Boolean })
  isEmptySelfFoldable = false;

  @property({ type: String })
  dragAnnouncementText = '';

  /**
   * Internal property
   * To handle the hover effect for row
   */
  @state()
  private isHovered = false;

  /**
   * The aria expanded attribute is toggled when the row is clicked/expanded
   * INFO - This is a public state and it got mutated in table-object
   */
  @state()
  isAriaExpanded: boolean | undefined;

  // Used to store the self foldable row associated with the parent row. Accessed in table-object
  public customRow: null | BlocksTableRow = null;

  /**
   * First updated lifecycle when component is first updated on page
   * 1) If is expandable add the aria-expanded attribute and set to false
   * 2) dispatched an internal event `bl-table-row-initialize` to table-object when any self-foldable row is initialized.
   * 3) Dispatching bl-table-row-prop-change to set Expandable rows when dynamically added row is Expandable Child.
   */
  firstUpdated() {
    setTimeout(() => {
      if (this.isExpandable === true || this.isExpandableNested) {
        this.isAriaExpanded = false; /* 1 */
      }
      this.setAriaLevel();
      /* 2 */
      if (this.isSelfFoldable && !this.hasAttribute('isCustomRow')) {
        this.dispatch({
          eventName: 'bl-table-row-initialize'
        });
      }
      /* 3 */
      if (this.isExpandableChild) {
        this.dispatch({
          eventName: 'bl-table-row-prop-change',
          detailObj: { propName: 'isExpandable' }
        });
      }
    }, 1);
  }

  private isBlocksTableCell(element: BlocksTableCell | BlocksTableHeaderCell): element is BlocksTableCell {
    return element.tagName.toLowerCase() === 'cat-table-cell';
  }

  /**
   *  @fire bl-table-dragText
   */
  private fireDraggedText(announcementText: string) {
    this.dispatch({
      eventName: 'bl-table-drag-text',
      detailObj: { rowDraggedText: announcementText }
    });
  }

  private resetCellProperties() {
    const allCells = this.querySelectorAll<BlocksTableCell | BlocksTableHeaderCell>('cat-table-cell, cat-table-header-cell');

    allCells.forEach((cell, index) => {
      if (index === 0) return;
      if (cell.isBulkAction) {
        cell.isBulkAction = false;
      }
      if (cell.isBulkActionSelected) {
        cell.isBulkActionSelected = false;
      }
      if (cell.isExpandable) {
        cell.isExpandable = false;
      }
      if (cell.isDraggable) {
        cell.isDraggable = false;
      }
      if (!this.isBlocksTableCell(cell)) return;

      if (cell.bulkActionIndex !== undefined) {
        cell.bulkActionIndex = undefined;
      }
      if (cell.isExpandableActive) {
        cell.isExpandableActive = false;
      }
      if (cell.isExpandableNestedChild) {
        cell.isExpandableNestedChild = false;
      }
    });
  }

  /**
   * This method is called on slot content change
   * Cascade row props to 1st column cell to render bulk-action, expandable and draggable icons properly when add/update row dynamically
   * 1. If table row is in disabled state, all the table cell part of that disabled row is set to disabled state. This is mainly done for accessibility announcement purpose.
   */
  private onSlotChange() {
    setTimeout(() => {
      //To reset the state of all the cells except the first cell. This is done in order to check when we update columns, the state gets reset
      this.resetCellProperties();

      // Now set properties on the first cell
      const firstCell = this.querySelector<BlocksTableCell | BlocksTableHeaderCell>(
        'cat-table-cell:first-of-type, cat-table-header-cell:first-of-type'
      );
      if (firstCell) {
        if (this.isBulkAction) {
          firstCell.isBulkAction = true;
          if (this.isBlocksTableCell(firstCell)) {
            firstCell.bulkActionIndex = this.bulkActionIndex;
          }
          if (this.isBulkActionSelected) {
            firstCell.isBulkActionSelected = true;
          } else {
            firstCell.isBulkActionSelected = false;
          }
        } else {
          firstCell.isBulkAction = false;
          firstCell.isBulkActionSelected = false;
        }
        //setting isExpandableNested is same as isExpandable for the nested rows
        if (this.isExpandable || this.isExpandableNested) {
          firstCell.isExpandable = this.isExpandable || this.isExpandableNested;
          if (this.isBlocksTableCell(firstCell)) {
            firstCell.isExpandableActive = this.isExpandableActive;
          }
        }

        if (this.isDraggable) {
          firstCell.isDraggable = this.isDraggable;
        }

        if (this.isExpandableNestedChild && this.isBlocksTableCell(firstCell)) {
          firstCell.isExpandableNestedChild = this.isExpandableNestedChild;
        }
        /* 1 */
        if (this.disabled) {
          this.setTableCellDisabled();
        }
      }
      this.dispatch({
        eventName: 'bl-table-cell-change',
        detailObj: {}
      });
      this.setLoadingState();
    }, 1);
  }

  /**
   * Set aria level attribute
   * 1. If any of the rows are expandable or expandable children, then set the appropriate aria-level for the role="treegrid"
   */
  private setAriaLevel() {
    /* 1 */
    if (this.isExpandable) {
      this.isAriaLevel = 1;
    } else if (this.isExpandableChild) {
      this.isAriaLevel = 2;
    } else if (this.isExpandableNestedChild) {
      this.isAriaLevel = 3;
    }
  }

  /**
   * Set draggable to first column cell
   */
  private setDraggable() {
    const rowFirstCell = this.querySelector<BlocksTableCell>('cat-table-cell:first-of-type');

    if (rowFirstCell) {
      rowFirstCell.isDraggable = this.isDraggable;
    }
  }

  private setLoadingState() {
    const rowTableCells = this.querySelectorAll<BlocksTableCell | BlocksTableHeaderCell>('cat-table-header-cell, cat-table-cell');
    rowTableCells.forEach((cell) => {
      cell.isLoading = this.isLoading;
    });
  }

  /* Set table cell to disabled state.*/
  private setTableCellDisabled() {
    const tableCells = this.querySelectorAll<BlocksTableCell>('cat-table-cell');
    tableCells.forEach((cell) => {
      cell.disabled = this.disabled;
    });
  }

  /**
   *
   * 1) Enable first cell draggable button if isDraggable is true on table-row
   * 2) Fire the draggable text to hidden element by dispatching an event
   * 3) Set table cell to disabled state
   */
  protected async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
    //TODO:Need to check why we need to trigger this multiple times
    if (_changedProperties.has('isDraggable')) {
      /**1 */
      this.setDraggable();
    }

    if (_changedProperties.has('isLoading')) {
      this.setLoadingState();
    }
    /**2 */
    if (_changedProperties.has('dragAnnouncementText')) {
      this.fireDraggedText(this.dragAnnouncementText);
    }
    /* 3 */
    if (_changedProperties.has('disabled')) {
      this.setTableCellDisabled();
    }

    // dispatched an internal event `bl-table-row-prop-change` to table-object when `isExpandable` and `isExpandableNestedChild` is toggled dynamically.
    if (_changedProperties.has('isExpandable') || _changedProperties.has('isExpandableNestedChild')) {
      await this.updateComplete;
      this.dispatch({
        eventName: 'bl-table-row-prop-change',
        detailObj: { propName: 'isExpandable' }
      });
    } else if (!(_changedProperties.size === 1 && _changedProperties.has('isHovered'))) {
      this.onSlotChange();
    }
  }

  /**
   * Disconnected callback lifecycle
   * 1. Remove the custom row created for this row
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.customRow?.remove();
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-table-row', {
      'cat-is-expandable': this.isExpandable === true,
      'cat-is-expandable-active': this.isExpandableActive === true,
      'cat-is-hovered': this.isHovered === true,
      'cat-is-expandable-child': this.isExpandableChild === true,
      'cat-is-expandable-child-active': this.isExpandableChildActive === true,
      'cat-is-expandable-nested': this.isExpandableNested === true,
      'cat-is-expandable-nested-child': this.isExpandableNestedChild === true,
      'cat-is-expandable-nested-child-active': this.isExpandableNestedChildActive === true,
      'cat-is-self-foldable': this.isSelfFoldable === true,
      'cat-is-self-foldable-child': this.isSelfFoldableChild === true,
      'cat-is-disabled': this.disabled === true,
      'cat-is-draggable': this.isDraggable === true,
      'cat-is-mobile-view': this.isMobileView === true,
      [`cat-is-drag-hover-${this.dragHover}`]: this.dragHover,
      'cat-is-row-bulkaction-selected': this.isBulkActionSelected === true,
      'cat-c-table-row--loader': this.isLoading === true
    });

    const tableValue = html`
      <div
        part="wrap"
        role=${ifDefined(!this.isLoading ? 'row' : undefined)}
        class="${componentClassName}"
        aria-level=${ifDefined(this.isAriaLevel)}
        data-key="${ifDefined(this.dataKey)}"
        @pointerover=${() => {
          this.isHovered = true;
        }}
        @pointerleave="${() => {
          this.isHovered = false;
        }}"
        @blur=${() => {
          this.dragAnnouncementText = '';
        }}
      >
        ${html`
          <slot
            part="part-default"
            @slotchange="${this.onSlotChange}"
            @bl-row-cell-select="${(event: BlocksRowCellSelectEvent) => (this.isBulkActionSelected = event.detail.isSelected)}"
          ></slot>
        `}
      </div>
    `;
    return this.disabled ? html`<div part="base" class="cat-c-table-row--disabled">${tableValue}</div>` : html`${tableValue}`;
  }
}

if (customElements.get('cat-table-row') === undefined) {
  customElements.define('cat-table-row', BlocksTableRow);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-table-row': BlocksTableRow;
  }
}
